import type { ReactNode } from 'react';
import type { Granularity } from '@/components/sales-chart';
import type {
  CampaignDto,
  TenantAppSessionsChartResponseDto,
  TenantUsersInstalledVersionsMetricsResponseDtoOutput,
  CampaignPerformanceResponseDto,
  RevenueChartResponseDto,
  TenantAppDownloadsChartResponseDto,
  TenantAovChartResponseDto,
  TenantCampaignOrdersChartResponseDto,
  TenantOrdersCompletionResponseDto,
  TenantRepurchaseRateChartResponseDto,
  TenantSalesMetricsResponseDto,
  TenantUserMetricsResponseDto,
} from '@/generated/korza-api-client/types.gen';
import type { DrilldownOrigin } from '@/lib/orders-drilldown';
import type { ChartRange } from '@/lib/partial-buckets';
import type { DeltaView } from '@/lib/period-comparison';

/** Variations of the Vendas cards against the previous period (ANLT-04). */
export interface SalesDeltas {
  revenue: DeltaView;
  averageTicket: DeltaView;
  orders: DeltaView;
  repurchases: DeltaView;
  /** App share in percentage points (CMP-04). */
  appShare: DeltaView;
}

/** All Analytics charts share it so the desktop hover moves them together. */
export const ANALYTICS_SYNC_ID = 'metrics-analytics-sync';

export type OpenDrilldown = (origin: DrilldownOrigin, bucket?: string) => void;

export interface DataSufficiencyState {
  minAppVersion: string;
  activeUsersOnOrAfterVersion: {
    count: number;
    percentage: number;
  };
  canDisplay: boolean;
  shouldShowWarning: boolean;
}

export interface SalesMetricsGridProps {
  salesMetrics: TenantSalesMetricsResponseDto | undefined;
  isLoadingSalesMetrics: boolean;
  dateFilterLabel: string;
  currency: string;
}

export interface CampaignMetricsGridProps {
  campaignPerformance: CampaignPerformanceResponseDto | undefined;
  isLoadingCampaignPerformance: boolean;
  isLoadingDataSufficiency?: boolean;
  dateFilterLabel: string;
  currency: string;
  campaignsSufficiency?: DataSufficiencyState;
}

export interface UsersMetricsGridProps {
  userMetrics: TenantUserMetricsResponseDto | undefined;
  isLoadingUserMetrics: boolean;
  isLoadingDataSufficiency?: boolean;
  dateFilterLabel: string;
  activeUsersSufficiency?: DataSufficiencyState;
  /** Downloads against the previous period (ANLT-06, CMP-01). */
  downloadsDelta?: DeltaView;
  /** "vs. …" next to the variation (CMP-03). */
  comparison?: ReactNode;
}

export interface MetricSectionsProps {
  salesMetrics: TenantSalesMetricsResponseDto | undefined;
  userMetrics: TenantUserMetricsResponseDto | undefined;
  campaignPerformance: CampaignPerformanceResponseDto | undefined;
  isLoadingSalesMetrics: boolean;
  isLoadingUserMetrics: boolean;
  isLoadingCampaignPerformance: boolean;
  dateFilterLabel: string;
  currency: string;
  granularity: Granularity;
  /** The filter's period, for the partial buckets of the charts (CHART-01). */
  chartRange?: ChartRange;
  /** Campaigns sent in the period (markers on the push charts, HOME-04). */
  periodCampaigns?: CampaignDto[];
  salesChart: RevenueChartResponseDto | undefined;
  aovChart: TenantAovChartResponseDto | undefined;
  repurchaseChart: TenantRepurchaseRateChartResponseDto | undefined;
  campaignOrdersChart: TenantCampaignOrdersChartResponseDto | undefined;
  appDownloadsChart: TenantAppDownloadsChartResponseDto | undefined;
  appDownloadsCumulativeChart: TenantAppDownloadsChartResponseDto | undefined;
  ordersCompletion: TenantOrdersCompletionResponseDto | undefined;
  isLoadingSalesChart: boolean;
  isLoadingAovChart: boolean;
  isLoadingRepurchaseChart: boolean;
  isLoadingCampaignOrdersChart: boolean;
  isLoadingAppDownloadsChart: boolean;
  isLoadingAppDownloadsCumulativeChart: boolean;
  isLoadingOrdersCompletion: boolean;
  isLoadingDataSufficiency: boolean;
  campaignsSufficiency?: DataSufficiencyState;
  activeUsersSufficiency?: DataSufficiencyState;
  /**
   * Opens the drill-down owned by the page; absent when analytics is off
   * (drill-downs only work with real data, not the mocked preview).
   */
  openDrilldown?: OpenDrilldown;
}

export type SalesSectionProps = Pick<
  MetricSectionsProps,
  | 'salesMetrics'
  | 'isLoadingSalesMetrics'
  | 'currency'
  | 'granularity'
  | 'chartRange'
  | 'salesChart'
  | 'aovChart'
  | 'repurchaseChart'
  | 'ordersCompletion'
  | 'isLoadingSalesChart'
  | 'isLoadingAovChart'
  | 'isLoadingRepurchaseChart'
  | 'isLoadingOrdersCompletion'
  | 'openDrilldown'
> & {
  /** Indicators shown under the title (the mobile Vendas tab's carousel). */
  kpis?: ReactNode;
  /** The previous period's revenue: the ghost line of Faturamento. */
  previousSalesChart?: RevenueChartResponseDto;
  deltas?: SalesDeltas;
  /** "vs. …" next to each variation (CMP-03). */
  comparison?: ReactNode;
};

export type CampaignsSectionProps = Pick<
  MetricSectionsProps,
  | 'campaignPerformance'
  | 'isLoadingCampaignPerformance'
  | 'isLoadingDataSufficiency'
  | 'dateFilterLabel'
  | 'currency'
  | 'campaignsSufficiency'
  | 'granularity'
  | 'chartRange'
  | 'periodCampaigns'
  | 'campaignOrdersChart'
  | 'isLoadingCampaignOrdersChart'
  | 'openDrilldown'
>;

export type UsersSectionProps = Pick<
  MetricSectionsProps,
  | 'userMetrics'
  | 'isLoadingUserMetrics'
  | 'isLoadingDataSufficiency'
  | 'dateFilterLabel'
  | 'activeUsersSufficiency'
  | 'granularity'
  | 'chartRange'
  | 'appDownloadsChart'
  | 'appDownloadsCumulativeChart'
  | 'isLoadingAppDownloadsChart'
  | 'isLoadingAppDownloadsCumulativeChart'
> &
  Pick<UsersMetricsGridProps, 'downloadsDelta' | 'comparison'> & {
    /** `/charts/app-sessions` (ANLT-06). */
    appSessionsChart?: TenantAppSessionsChartResponseDto;
    isLoadingAppSessionsChart?: boolean;
    /** `/metrics/users/installed-versions` (ANLT-06). */
    installedVersions?: TenantUsersInstalledVersionsMetricsResponseDtoOutput;
  };
